import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { brl } from '../../utils/format';
import CategoriaSelect from '../../components/CategoriaSelect';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const mesLabel = (mes) => {
  const [y, m] = mes.split('-');
  return `${MESES[Number(m) - 1]} de ${y}`;
};

const addMonth = (mes, delta) => {
  let [y, m] = mes.split('-').map(Number);
  m += delta;
  if (m < 1) { m = 12; y -= 1; }
  if (m > 12) { m = 1; y += 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
};

const emptyForm = () => ({ tipo: 'fixa', categoria: '', valor: '', descricao: '', data_referencia: new Date().toISOString().slice(0, 10) });

export default function Despesas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [mes, setMes] = useState(mesAtual());
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { load(); }, [mes, filtroTipo, filtroCategoria]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { mes };
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroCategoria) params.categoria = filtroCategoria;
      const res = await api.get('/expenses/', { params });
      setItems(res.data);
    } catch { toast.error('Erro ao carregar despesas'); }
    finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/settings/');
      setCategories(res.data.categorias_despesa || []);
    } catch {}
  };

  const addCategoria = async (nome) => {
    const novaLista = [...categories, nome];
    await api.put('/settings/', { categorias_despesa: novaLista });
    setCategories(novaLista);
  };

  const openModal = (item = null) => {
    setEditing(item);
    setForm(item ? { ...item, valor: item.valor } : emptyForm());
    setShowModal(true);
  };

  const save = async () => {
    if (!form.categoria) { toast.error('Categoria obrigatória'); return; }
    if (!form.valor || Number(form.valor) <= 0) { toast.error('Informe um valor válido'); return; }
    setSaving(true);
    try {
      const payload = { ...form, valor: Number(form.valor) };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success('Despesa atualizada');
      } else {
        await api.post('/expenses/', payload);
        toast.success('Despesa cadastrada');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Excluir despesa?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Despesa excluída');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  const total = items.reduce((s, i) => s + i.valor, 0);
  const totalFixas = items.filter(i => i.tipo === 'fixa').reduce((s, i) => s + i.valor, 0);
  const totalVariaveis = items.filter(i => i.tipo === 'variavel').reduce((s, i) => s + i.valor, 0);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Despesas Mensais</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Custos fixos e variáveis da empresa</p>
        </div>
        <button onClick={() => openModal()} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-4 py-2 rounded-xl transition-colors">
          <Plus size={17} /> Nova despesa
        </button>
      </div>

      {/* Navegação de mês */}
      <div className="flex items-center justify-center gap-4">
        <button onClick={() => setMes(m => addMonth(m, -1))} className="p-2 text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors"><ChevronLeft size={16} /></button>
        <span className="text-sm font-medium text-white w-40 text-center">{mesLabel(mes)}</span>
        <button onClick={() => setMes(m => addMonth(m, 1))} className="p-2 text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors"><ChevronRight size={16} /></button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total do mês', value: total, color: 'text-white' },
          { label: 'Fixas', value: totalFixas, color: 'text-orange-400' },
          { label: 'Variáveis', value: totalVariaveis, color: 'text-zinc-300' },
        ].map(k => (
          <div key={k.label} className="stat-card">
            <div className="text-xs text-zinc-500 mb-1">{k.label}</div>
            <div className={`font-mono font-bold text-lg ${k.color}`}>{brl(k.value)}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        {[{ v: '', l: 'Todos' }, { v: 'fixa', l: 'Fixas' }, { v: 'variavel', l: 'Variáveis' }].map(f => (
          <button
            key={f.v}
            onClick={() => setFiltroTipo(f.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filtroTipo === f.v ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
          >
            {f.l}
          </button>
        ))}
        <select className="field-select ml-2 w-48" value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}>
          <option value="">Todas as categorias</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800">
            <tr className="text-xs text-zinc-500">
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Categoria</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Descrição</th>
              <th className="text-right px-4 py-3">Valor</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12"><div className="inline-block w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : items.map(item => (
              <tr key={item.id} className="table-row-hover">
                <td className="px-4 py-3 text-zinc-400">{item.data_referencia.split('-').reverse().join('/')}</td>
                <td className="px-4 py-3">
                  <span className={`badge ${item.tipo === 'fixa' ? 'bg-orange-500/20 text-orange-400' : 'bg-zinc-700 text-zinc-300'}`}>
                    {item.tipo === 'fixa' ? 'Fixa' : 'Variável'}
                  </span>
                </td>
                <td className="px-4 py-3 text-white">{item.categoria}</td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{item.descricao || '—'}</td>
                <td className="px-4 py-3 text-right font-mono text-white">{brl(item.valor)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openModal(item)} className="p-1.5 text-zinc-500 hover:text-orange-400 transition-colors"><Edit size={14} /></button>
                    <button onClick={() => del(item.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm">Nenhuma despesa cadastrada neste mês</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <h2 className="font-heading font-semibold text-white">{editing ? 'Editar' : 'Nova'} despesa</h2>
              <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="field-label">Tipo *</label>
                <div className="flex gap-4 mt-1">
                  {[{ v: 'fixa', l: 'Fixa' }, { v: 'variavel', l: 'Variável' }].map(o => (
                    <label key={o.v} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input type="radio" name="tipo" checked={form.tipo === o.v} onChange={() => setForm(p => ({ ...p, tipo: o.v }))} />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">Categoria da Despesa *</label>
                <CategoriaSelect
                  categories={categories}
                  value={form.categoria}
                  onChange={(v) => setForm(p => ({ ...p, categoria: v }))}
                  onAddCategory={addCategoria}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Valor (R$) *</label>
                  <input type="text" inputMode="decimal" className="field-input" value={form.valor} onChange={e => setForm(p => ({ ...p, valor: e.target.value }))} />
                </div>
                <div>
                  <label className="field-label">Data de referência/vencimento *</label>
                  <input type="date" className="field-input" value={form.data_referencia} onChange={e => setForm(p => ({ ...p, data_referencia: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="field-label">Descrição</label>
                <input className="field-input" value={form.descricao || ''} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">Cancelar</button>
              <button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
